import React from 'react';
import { Line, Circle, Group as KonvaGroup } from 'react-konva';
import VertexHandle from './VertexHandle';
import TextLabelShape from './TextLabelShape';

const ArrowShape = ({
  shape,
  commonProps,
  isSelected,
  isHovered,
  stageScale,
  selectionStrokeWidth,
  calculatedStrokeWidth,
  vertexMarkerRadius,
  selectedStrokeHighlightColor,
  hoverColor,
  onVertexClick,
  onStartTextEdit,
  textEditingShapeId,
  onUpdateShape,
  shapeStroke,
}) => {
  const [origX1, origY1, x2, y2] = shape.points;
  const L_orig = Math.sqrt((x2 - origX1) * (x2 - origX1) + (y2 - origY1) * (y2 - origY1));

  if (L_orig === 0) return null;

  // Let tail (x1, y1) follow textOffsetX/Y if stored
  const x1 = shape.textOffsetX !== undefined ? shape.textOffsetX : origX1;
  const y1 = shape.textOffsetY !== undefined ? shape.textOffsetY : origY1;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const L = Math.sqrt(dx * dx + dy * dy);

  const angle = Math.atan2(dy, dx);

  // Arrowhead size and geometry (open V-wings design)
  const arrowSize = Math.max(
    12 / stageScale,
    6 * (isSelected || isHovered ? selectionStrokeWidth : calculatedStrokeWidth)
  );
  const phi = Math.PI / 6; // 30 degree wings (60 degree total opening)

  const p1X = x2 - arrowSize * Math.cos(angle - phi);
  const p1Y = y2 - arrowSize * Math.sin(angle - phi);
  const p2X = x2 - arrowSize * Math.cos(angle + phi);
  const p2Y = y2 - arrowSize * Math.sin(angle + phi);

  // Tail pinpoint circle size
  const tailRadius = Math.max(3 / stageScale, calculatedStrokeWidth * 1.5);

  const SELECTED_GLOW = {
    shadowColor: '#3B82F6',
    shadowBlur: 18,
    shadowOpacity: 0.7,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
  };

  // Default text offset to first point (tail) of arrow if not stored
  const defaultTextOffsetX = origX1;
  const defaultTextOffsetY = origY1;
  const shapeWithDefaults = {
    ...shape,
    textOffsetX: shape.textOffsetX !== undefined ? shape.textOffsetX : defaultTextOffsetX,
    textOffsetY: shape.textOffsetY !== undefined ? shape.textOffsetY : defaultTextOffsetY,
  };

  // Adjust starting point to not cover the text label
  const title = shape.text ? shape.text.split('\n---\n')[0] : (shape.id.slice(-10));
  const body = shape.text && shape.text.split('\n---\n')[1];
  const titleFontSize = Math.max(12, 16 / stageScale, (commonProps.strokeWidth || 2) * 3);
  const bodyFontSize = Math.max(12, 12 / stageScale, (commonProps.strokeWidth || 2) * 2);

  const textWidth = Math.max(title.length, body ? 15 : 0) * titleFontSize * 0.6;
  const textHeight = titleFontSize + (body ? body.split('\n').length * bodyFontSize + 10 : 0);

  let t_exit = 0;
  if (dx > 0 && dy > 0) {
    t_exit = Math.min(textWidth / dx, textHeight / dy);
  } else if (dx > 0 && dy === 0) {
    t_exit = textWidth / dx;
  } else if (dy > 0 && dx === 0) {
    t_exit = textHeight / dy;
  }
  // NEVER let the text offset consume more than 35% of the arrow's total length.
  // This ensures the line is never made "too small" for short arrows or edge cases.
  t_exit = Math.min(t_exit, 0.35);

  const paddingPx = 8 / stageScale;
  const startX = x1 + t_exit * dx + (L > 0 ? (paddingPx * dx / L) : 0);
  const startY = y1 + t_exit * dy + (L > 0 ? (paddingPx * dy / L) : 0);

  // If the starting point is past the tip, adjust it or don't draw the shaft
  const shaftDx = x2 - startX;
  const shaftDy = y2 - startY;
  const drawShaft = L > 0 && (shaftDx * dx + shaftDy * dy) > 0;

  return (
    <KonvaGroup {...commonProps}>
      {/* Arrow Shaft (drawn from tail to the tip x2/y2) */}
      {drawShaft && (
        <Line
          name="visual_shape"
          points={[startX, startY, x2, y2]}
          stroke={commonProps.stroke}
          strokeWidth={commonProps.strokeWidth}
          lineCap="round"
          perfectDrawEnabled={false}
          listening={true}
          {...(isSelected ? SELECTED_GLOW : {})}
        />
      )}
      {/* Pinpoint Dot at Start (Tail) */}
      <Circle
        x={startX}
        y={startY}
        radius={tailRadius}
        fill={commonProps.stroke}
        perfectDrawEnabled={false}
        listening={false}
      />
      {/* Open V-Wings Arrowhead */}
      <Line
        points={[p1X, p1Y, x2, y2, p2X, p2Y]}
        stroke={commonProps.stroke}
        strokeWidth={commonProps.strokeWidth}
        lineCap="round"
        lineJoin="round"
        perfectDrawEnabled={false}
        listening={false}
      />
      {/* Text label (title + body + anchor) — delegated to TextLabelShape */}
      <TextLabelShape
        shape={shapeWithDefaults}
        commonProps={commonProps}
        isSelected={isSelected}
        stageScale={stageScale}
        shapeStroke={shapeStroke}
        onStartTextEdit={onStartTextEdit}
        textEditingShapeId={textEditingShapeId}
        onUpdateShape={onUpdateShape}
      />
      {/* Vertex handles for selected Arrow */}
      {isSelected &&
        shape.points &&
        shape.points.map((_, index) => {
          if (index % 2 === 0 && index !== 0) { // Skip index 0 (tail), only render index 2 (tip)
            const vertexX = shape.points[index];
            const vertexY = shape.points[index + 1];
            return (
              <VertexHandle
                key={`vtx-arrow-${shape.id}-${index / 2}`}
                x={vertexX}
                y={vertexY}
                radius={vertexMarkerRadius}
                color={selectedStrokeHighlightColor}
                hoverColor={hoverColor}
                onMouseDown={(e) => onVertexClick(e, shape.id, index / 2)}
                onTouchStart={(e) => onVertexClick(e, shape.id, index / 2)}
                stageScale={stageScale}
              />
            );
          }
          return null;
        })}
    </KonvaGroup>
  );
};

export default ArrowShape;
